import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Folder, Loader2, Lock, RotateCcw, Search } from "lucide-react";
import ProjectActions from "@/components/creator/music/ProjectActions";
import StagePicker from "@/components/creator/music/StagePicker";
import { coverSrc } from "@/lib/creatorApi";
import { STAGES } from "@/lib/mixDeskApi";
import { deleteProject, fmtAgo, listFolders, listProjects, restoreProject } from "@/lib/studioApi";
import { safeApiError } from "@/lib/safeErrorText";

const ALL = "__all__";
const DELETED = "__deleted__";

function ProjectRow({ p, deleted, onOpen, onReload }) {
  const cover = coverSrc(p);
  const running = p.generation?.status === "running";
  const restore = async () => { try { await restoreProject(p.id); toast.success(`"${p.title}" restored`); onReload(); } catch (e) { toast.error("Couldn't restore", { description: safeApiError(e) }); } };
  const hardDelete = async () => {
    if (!window.confirm(`Delete "${p.title}" forever? Its songs stay in your Songs tab.`)) return;
    try { await deleteProject(p.id, { hard: true }); toast.message("Deleted forever"); onReload(); } catch (e) { toast.error("Couldn't delete", { description: safeApiError(e) }); }
  };
  return (
    <div data-testid={`library-project-${p.id}`} className="flex flex-col gap-3 rounded-2xl border border-white/[0.08] bg-[#0a0d1c]/80 p-3 transition-colors hover:bg-white/[0.04] md:flex-row md:items-center md:gap-4">
      <div className="flex min-w-0 flex-1 items-center gap-4">
      <button type="button" onClick={() => onOpen(p)} className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-gradient-to-br from-blue-600/40 to-amber-500/30" aria-label={`Open ${p.title}`}>
        {cover ? <img src={cover} alt="" className="h-full w-full object-cover" /> : <span className="grid h-full place-items-center font-display text-xl font-bold text-white/40">{(p.title || "E")[0]}</span>}
      </button>
      <div className="min-w-0 flex-1">
        <p data-testid={`library-project-title-${p.id}`} className="truncate font-display text-sm font-semibold">{p.title || "Untitled song"}</p>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-white/45">
          <span>{p.artist}</span><span>{p.genre_label}</span>
          <span>{p.versions_count} version{p.versions_count === 1 ? "" : "s"}</span>
          {p.lyrics_lock ? <span className="inline-flex items-center gap-0.5 text-emerald-300/80"><Lock className="h-3 w-3" />Locked</span> : null}
          {p.folder ? <span className="inline-flex items-center gap-1 text-blue-200/80"><Folder className="h-3 w-3" />{p.folder}</span> : null}
          {running ? <span className="inline-flex items-center gap-1 text-blue-200"><Loader2 className="h-3 w-3 animate-spin" />Creating</span> : null}
          <span>Edited {fmtAgo(p.updated_at)}</span>
        </p>
      </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1.5">
        {deleted ? (
          <>
            <button type="button" data-testid={`library-project-restore-${p.id}`} onClick={restore} className="inline-flex items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-emerald-200"><RotateCcw className="h-3 w-3" /> Restore</button>
            <button type="button" data-testid={`library-project-hard-delete-${p.id}`} onClick={hardDelete} className="rounded-full border border-white/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white/55 hover:text-rose-300">Delete forever</button>
          </>
        ) : (
          <>
            <button type="button" data-testid={`library-project-open-${p.id}`} onClick={() => onOpen(p)} className="rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white">Open</button>
            <StagePicker project={p} onChanged={onReload} />
            <ProjectActions project={p} compact onChanged={onReload} onDeleted={onReload} />
          </>
        )}
      </div>
    </div>
  );
}

/** Projects with albums / collections, status labels, search, rename / duplicate / move / delete / restore. */
export default function ProjectsTab({ onCount }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState(ALL);
  const [stage, setStage] = useState(null);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [folders, setFolders] = useState([]);

  const load = useCallback(async () => {
    const params = { q: q || undefined, stage: stage || undefined, deleted: filter === DELETED || undefined, folder: filter !== ALL && filter !== DELETED ? filter : undefined };
    try {
      const [list, f] = await Promise.all([listProjects(params), listFolders()]);
      setRows(list); setFolders(f);
      if (filter === ALL && !q && !stage) onCount?.(list.length);
    } catch (e) {
      setRows([]);
      toast.error("Couldn't load your projects", { description: safeApiError(e) });
    }
  }, [filter, q, stage, onCount]);
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [load, q]);
  useEffect(() => {
    if (!rows?.some((p) => p.generation?.status === "running")) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [rows, load]);

  const chip = (key, label, testId) => (
    <button key={key} type="button" data-testid={testId} aria-pressed={filter === key} onClick={() => setFilter(key)} className={`rounded-full border px-3 py-1.5 text-[12px] transition-colors ${filter === key ? "border-blue-400/60 bg-blue-500/20 text-white" : "border-white/10 bg-white/[0.03] text-white/65 hover:bg-white/[0.08]"}`}>{label}</button>
  );

  return (
    <div data-testid="library-projects">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="relative mr-2 min-w-[220px] flex-1 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
          <input data-testid="library-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, artist, album or lyrics" className="w-full rounded-full border border-white/10 bg-black/40 py-2 pl-9 pr-3 text-sm text-white placeholder:text-white/30 focus:border-blue-400/60 focus:outline-none" />
        </label>
        {chip(ALL, "All projects", "library-folder-all")}
        {folders.length ? <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white/35"><Folder className="h-3 w-3" />Albums</span> : null}
        {folders.map((f) => chip(f.name, `${f.name} · ${f.count}`, `library-folder-${f.name.replace(/\W+/g, "-").toLowerCase()}`))}
        {chip(DELETED, "Deleted", "library-folder-deleted")}
        <Link to="/create/song" data-testid="library-new-project" className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-white">New song <ArrowRight className="h-3 w-3" /></Link>
      </div>
      <div data-testid="library-stage-filters" className="mb-4 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white/35">Status</span>
        {[{ key: null, label: "Any" }, ...STAGES].map((s) => (
          <button key={s.key || "any"} type="button" data-testid={`library-stage-${s.key || "any"}`} aria-pressed={stage === s.key} onClick={() => setStage(s.key)}
            className={`rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors duration-200 ${stage === s.key ? (s.tone || "border-blue-400/60 bg-blue-500/20 text-white") : "border-white/10 text-white/50 hover:text-white"}`}>{s.label}</button>
        ))}
      </div>
      {rows === null ? <div className="flex items-center gap-2 text-white/60"><Loader2 className="h-4 w-4 animate-spin" /> Loading your projects…</div>
        : rows.length ? <div className="grid gap-2">{rows.map((p) => <ProjectRow key={p.id} p={p} deleted={filter === DELETED} onOpen={(x) => navigate(`/create/project/${x.id}`)} onReload={load} />)}</div>
          : <div data-testid="library-projects-empty" className="rounded-3xl border border-dashed border-white/10 p-10 text-center text-white/55">{filter === DELETED ? "Nothing in Deleted." : q || stage ? "No projects match." : "No projects yet — create your first song."}</div>}
    </div>
  );
}
